import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { formatNaira } from "@/lib/api";

export default function CartPage() {
  const { items, updateQuantity, removeFromCart, cartTotal, cartCount } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const shipping = cartTotal > 5000 || cartTotal === 0 ? 0 : 500;
  const grandTotal = cartTotal + shipping;

  const handleCheckout = () => {
    if (!user) {
      navigate("/login?next=/checkout");
    } else {
      navigate("/checkout");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16" data-testid="cart-page">
      <p className="label-eyebrow mb-3">Bag</p>
      <h1 className="font-serif text-5xl tracking-tighter mb-12">Your cart ({cartCount})</h1>

      {items.length === 0 ? (
        <div className="text-center py-24">
          <p className="text-[#6A747D] mb-6">Your cart is currently empty.</p>
          <Link to="/shop" className="btn-gold" data-testid="cart-empty-shop-btn">Continue Shopping</Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-6" data-testid="cart-items-list">
            {items.map((it) => (
              <div key={it.product.id} className="flex gap-6 pb-6 border-b border-[#E8E2D9]" data-testid={`cart-item-${it.product.id}`}>
                <Link to={`/product/${it.product.id}`} className="w-24 h-32 bg-[#F3EFEA] flex-shrink-0 overflow-hidden">
                  <img src={it.product.image} alt={it.product.name} className="w-full h-full object-cover" />
                </Link>
                <div className="flex-1 flex flex-col justify-between">
                  <div>
                    <p className="label-eyebrow">{(it.product.categories || []).join(", ")}</p>
                    <Link to={`/product/${it.product.id}`}><h3 className="font-serif text-xl mt-1">{it.product.name}</h3></Link>
                    <p className="text-sm text-[#6A747D] mt-1">{formatNaira(it.product.price)} each</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center border border-[#E8E2D9]">
                      <button onClick={() => updateQuantity(it.product.id, it.quantity - 1)} className="px-2 py-2 hover:bg-[#F3EFEA]" data-testid={`cart-decrease-${it.product.id}`}><Minus size={14} /></button>
                      <span className="px-4 text-sm">{it.quantity}</span>
                      <button onClick={() => updateQuantity(it.product.id, it.quantity + 1)} className="px-2 py-2 hover:bg-[#F3EFEA]" data-testid={`cart-increase-${it.product.id}`}><Plus size={14} /></button>
                    </div>
                    <p className="font-medium">{formatNaira(it.product.price * it.quantity)}</p>
                  </div>
                </div>
                <button onClick={() => removeFromCart(it.product.id)} className="text-[#6A747D] hover:text-[#333E48]" data-testid={`cart-remove-${it.product.id}`}><X size={20} /></button>
              </div>
            ))}
          </div>

          <aside className="bg-[#F3EFEA] p-8 h-fit" data-testid="cart-summary">
            <h2 className="font-serif text-2xl mb-6">Order Summary</h2>
            <div className="space-y-3 text-sm border-b border-[#E8E2D9] pb-6">
              <div className="flex justify-between"><span>Subtotal</span><span>{formatNaira(cartTotal)}</span></div>
              <div className="flex justify-between"><span>Shipping</span><span>{shipping === 0 ? "Free" : formatNaira(shipping)}</span></div>
            </div>
            <div className="flex justify-between mt-6 mb-8 font-serif text-xl"><span>Total</span><span data-testid="cart-total">{formatNaira(grandTotal)}</span></div>
            <button onClick={handleCheckout} className="btn-gold w-full" data-testid="checkout-btn">Proceed to Checkout</button>
            <Link to="/shop" className="block text-center mt-4 text-xs uppercase tracking-[0.2em] text-[#6A747D] hover:text-[#333E48]">Continue Shopping</Link>
          </aside>
        </div>
      )}
    </div>
  );
}
